import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import CartaoErro from '../../componentes/CartaoErro'
import type { ItemPedido, SubPedido } from '../../db/tipos'
import { devolverItem } from '../../pedidos/carrinho'
import { cancelarSubPedido, mudarStatusSubPedido } from '../../pedidos/fechamento'
import { MOTIVOS_AJUSTE, MOTIVOS_DEVOLUCAO } from '../../pedidos/ajustes'
import { formatarMoeda } from '../../lib/formatos'
import { converterNumeroBR } from '../../lib/textoBR'
import { calcularTotalItem } from '../../pedidos/precos'
import ItensDoSubPedido from './ItensDoSubPedido'

interface Props {
  subPedido: SubPedido
  aoFechar: () => void
}

// As tres saidas da conferencia da entrega, do fluxo que ela descreveu em
// 2026-07-29: "entrega confirmada, devolucao, nota cancelada".
type Saida = null | 'devolucao' | 'nota_cancelada'

export default function ConfirmarEntrega({ subPedido, aoFechar }: Props) {
  const [saida, setSaida] = useState<Saida>(null)
  // Itens marcados como devolvidos, cada um com a SUA quantidade e o SEU
  // motivo. A quantidade e texto porque o campo aceita virgula -- o projeto ja
  // perdeu numero em silencio por converter cedo demais.
  const [marcados, setMarcados] = useState<Record<string, { quantidade: string; motivo: string }>>({})
  const [motivoCancelamento, setMotivoCancelamento] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [devolveu, setDevolveu] = useState(false)

  const itens = useLiveQuery(
    () =>
      banco.itens_pedido
        .where('sub_pedido_id')
        .equals(subPedido.id)
        .filter((i) => i.apagado_em === null)
        .toArray(),
    [subPedido.id],
  )
  const produtos = useLiveQuery(
    () => banco.produtos.where('representada_id').equals(subPedido.representada_id).toArray(),
    [subPedido.representada_id],
  )
  const nomeProduto = (id: string) => produtos?.find((p) => p.id === id)?.descricao ?? id

  // Guardas podem recusar se uma sincronizacao mudou o status entre o render e o
  // clique. A recusa vira aviso na tela, nunca silencio.
  async function tentar(acao: () => Promise<void>) {
    setErro(null)
    try {
      await acao()
      return true
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Não foi possível concluir.')
      return false
    }
  }

  async function confirmarEntrega() {
    // Sem motivo: nao houve perda. So falta o pagamento do cliente.
    if (await tentar(() => mudarStatusSubPedido(subPedido, 'entregue'))) aoFechar()
  }

  function alternarItem(item: ItemPedido, marcado: boolean) {
    setErro(null)
    setMarcados((atual) => {
      const copia = { ...atual }
      // Ja vem com a quantidade CHEIA: devolver tudo e o caso mais comum, e
      // reduzir e mais facil do que digitar do zero.
      if (marcado) copia[item.id] = { quantidade: String(item.quantidade), motivo: '' }
      else delete copia[item.id]
      return copia
    })
  }

  function mudarQuantidade(itemId: string, texto: string) {
    setErro(null)
    setMarcados((atual) =>
      atual[itemId] ? { ...atual, [itemId]: { ...atual[itemId], quantidade: texto } } : atual,
    )
  }

  function mudarMotivo(itemId: string, codigo: string) {
    setMarcados((atual) =>
      atual[itemId] ? { ...atual, [itemId]: { ...atual[itemId], motivo: codigo } } : atual,
    )
  }

  // Quanto voltou daquele item, ou null se o que esta escrito nao serve. A
  // guarda de verdade mora em devolverItem; aqui e so para nao habilitar o
  // botao com um numero impossivel.
  function quantidadeValida(item: ItemPedido, texto: string): number | null {
    const valor = converterNumeroBR(texto)
    if (valor === null || valor <= 0 || valor > item.quantidade) return null
    return valor
  }

  // O que vai acontecer se ela confirmar com o que esta escrito. Aparece
  // enquanto ela digita: e a diferenca entre "voltaram 8" e "ficam 8".
  function resultado(item: ItemPedido, texto: string): string {
    const devolvida = converterNumeroBR(texto)
    if (devolvida === null || devolvida <= 0) return 'Digite quantas unidades voltaram.'
    if (devolvida > item.quantidade) {
      return `Só saíram ${item.quantidade} un deste produto.`
    }
    const restante = item.quantidade - devolvida
    if (restante === 0) return 'Volta tudo: o produto sai do pedido.'
    const valorRestante = calcularTotalItem(restante, item.preco_unitario, item.desconto_pct)
    return `Ficam ${restante} un no pedido (${formatarMoeda(valorRestante)}).`
  }

  const itensMarcados = (itens ?? []).filter((i) => marcados[i.id] !== undefined)
  const quantosMarcados = itensMarcados.length
  const podeDevolver =
    quantosMarcados > 0 &&
    itensMarcados.every(
      (i) =>
        marcados[i.id].motivo !== '' && quantidadeValida(i, marcados[i.id].quantidade) !== null,
    )

  async function confirmarDevolucao() {
    if (!podeDevolver) return
    setErro(null)
    // Um a um, na ordem da tela: cada item tem motivo proprio, e devolver o
    // ultimo item inteiro cancela o sub-pedido dentro da propria chamada.
    for (const item of itensMarcados) {
      const escolha = marcados[item.id]
      const quantidade = quantidadeValida(item, escolha.quantidade)
      if (quantidade === null) continue
      const ok = await tentar(() => devolverItem(item, escolha.motivo, quantidade))
      if (!ok) return // para na primeira recusa: o resto continua marcado na tela
    }
    setMarcados({})
    setDevolveu(true)
  }

  async function confirmarNotaCancelada() {
    if (!motivoCancelamento) return
    if (await tentar(() => cancelarSubPedido(subPedido, motivoCancelamento))) aoFechar()
  }

  return (
    <div className="mt-2 space-y-2 bg-white border border-slate-200 rounded-xl p-3">
      {saida === null && (
        <>
          <p className="text-sm text-slate-700">
            A entrega de {subPedido.letra} ({formatarMoeda(subPedido.valor_total)}) chegou ao
            cliente?
          </p>
          {/* O pedido na tela na hora de confirmar: antes eu pedia para ela
              confirmar a entrega sem mostrar o que estava sendo confirmado
              (achado dela em 2026-07-30). Depois de uma devolucao, e aqui que
              ela confere o que ficou. */}
          <ItensDoSubPedido subPedido={subPedido} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void confirmarEntrega()}
              className="bg-teal-700 text-white rounded px-3 py-1 text-sm"
            >
              Entrega confirmada
            </button>
            <button
              type="button"
              onClick={() => {
                setSaida('devolucao')
                setMarcados({})
                setErro(null)
              }}
              className="border border-amber-600 text-amber-700 rounded px-3 py-1 text-sm"
            >
              Devolução
            </button>
            <button
              type="button"
              onClick={() => {
                setSaida('nota_cancelada')
                setMotivoCancelamento('')
                setErro(null)
              }}
              className="border border-red-300 text-red-600 rounded px-3 py-1 text-sm"
            >
              Nota cancelada
            </button>
          </div>
        </>
      )}

      {saida === 'devolucao' && (
        <>
          <p className="text-sm text-slate-700">
            Marque o que voltou. Nem sempre volta o produto inteiro — a
            quantidade já vem cheia e você reduz para o que foi devolvido.
          </p>
          <ul className="space-y-1">
            {(itens ?? []).map((item) => {
              const marcado = marcados[item.id]
              return (
                <li key={item.id} className="border-t border-slate-100 pt-1">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={marcado !== undefined}
                      onChange={(e) => alternarItem(item, e.target.checked)}
                      aria-label={`Devolver ${nomeProduto(item.produto_id)}`}
                    />
                    <span className="flex-1">{nomeProduto(item.produto_id)}</span>
                    <span className="text-slate-700">{item.quantidade} un</span>
                    {/* Mesma leitura de Ajustar itens: sem o unitario ela nao
                        tem como conferir a conta da linha. */}
                    <span className="text-slate-500 whitespace-nowrap">
                      × {formatarMoeda(item.preco_unitario)}
                      {item.desconto_pct > 0 && (
                        <span className="text-amber-700"> −{item.desconto_pct}%</span>
                      )}
                    </span>
                    <span className="text-slate-700 font-medium whitespace-nowrap">
                      = {formatarMoeda(item.total_item)}
                    </span>
                  </label>

                  {/* Marcou: abre a quantidade devolvida e, so entao, o motivo
                      -- um motivo POR ITEM (decisao dela em 2026-07-30): dois
                      produtos podem ter voltado por razoes diferentes na mesma
                      conferencia. */}
                  {marcado !== undefined && (
                    <div className="ml-6 my-1 bg-amber-50 border border-amber-200 rounded-xl p-2 space-y-1">
                      <label className="flex items-center gap-2 text-xs text-slate-700">
                        Voltaram
                        <input
                          aria-label={`Quantidade devolvida de ${nomeProduto(item.produto_id)}`}
                          inputMode="numeric"
                          value={marcado.quantidade}
                          onChange={(e) => mudarQuantidade(item.id, e.target.value)}
                          className="w-16 border border-slate-300 rounded px-1 py-0.5 text-right"
                        />
                        de {item.quantidade} un
                      </label>
                      {/* A conta na hora, porque esta caixa e a caixa de
                          "Ajustar itens" sao iguais e querem dizer o OPOSTO:
                          la se digita quanto FICA, aqui quanto VOLTOU. Ela
                          digitou 8 querendo deixar 8 e sobraram 2 (smoke de
                          2026-07-30) -- ver o resultado enquanto digita e o
                          que desfaz a ambiguidade. */}
                      <p className="text-xs text-slate-700">{resultado(item, marcado.quantidade)}</p>
                      {MOTIVOS_DEVOLUCAO.map((m) => (
                        <label
                          key={m.codigo}
                          className="flex items-center gap-2 text-sm text-slate-700"
                        >
                          <input
                            type="radio"
                            name={`motivo-devolucao-${item.id}`}
                            checked={marcado.motivo === m.codigo}
                            onChange={() => mudarMotivo(item.id, m.codigo)}
                            aria-label={m.rotulo}
                          />
                          {m.rotulo}
                        </label>
                      ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>

          {quantosMarcados > 0 && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void confirmarDevolucao()}
                disabled={!podeDevolver}
                className="bg-amber-700 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
              >
                Confirmar devolução
                {quantosMarcados > 1 ? ` (${quantosMarcados} itens)` : ''}
              </button>
              <button
                type="button"
                onClick={() => setMarcados({})}
                className="text-slate-600 text-sm underline"
              >
                Desmarcar tudo
              </button>
            </div>
          )}

          {devolveu && (
            <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
              Os itens mudaram. O pedido continua faturado — confirme a entrega do que ficou.
            </p>
          )}
        </>
      )}

      {saida === 'nota_cancelada' && (
        <>
          <p className="text-sm text-slate-700">
            Cancelando a nota de {subPedido.letra} ({formatarMoeda(subPedido.valor_total)}). Por
            quê?
          </p>
          {/* A MESMA lista fechada do Cancelar: e o mesmo ato -- perder a marca
              inteira -- num estagio mais tarde. */}
          {MOTIVOS_AJUSTE.map((m) => (
            <label key={m.codigo} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="motivo-nota-cancelada"
                checked={motivoCancelamento === m.codigo}
                onChange={() => setMotivoCancelamento(m.codigo)}
                aria-label={m.rotulo}
              />
              {m.rotulo}
            </label>
          ))}
          <button
            type="button"
            onClick={() => void confirmarNotaCancelada()}
            disabled={!motivoCancelamento}
            className="bg-red-600 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
          >
            Confirmar cancelamento
          </button>
        </>
      )}

      {erro && <CartaoErro titulo="Não foi possível confirmar">{erro}</CartaoErro>}

      <button
        type="button"
        onClick={() => (saida === null ? aoFechar() : setSaida(null))}
        className="block text-slate-600 text-sm underline"
      >
        {saida === null ? 'Fechar' : 'Voltar'}
      </button>
    </div>
  )
}
